// Central game registry — single source of truth for game metadata.
// IDs here must match the gameType strings used throughout the codebase.

// 'playable': finished enough to play for real; 'in-progress': still being designed (shown apart, as in
// development); 'placeholder': not shown.
export type GameStatus = 'playable' | 'in-progress' | 'placeholder';

export interface RulesetDef {
  id: string;
  name: string;
  description: string;
  config: Record<string, unknown>;
}

export interface GameDef {
  id: string;
  name: string;
  description: string;
  minPlayers: number;
  maxPlayers: number;
  rulesets: readonly RulesetDef[];
  status: GameStatus;
  hasModule: boolean;   // true when src/games/<id>/<id>.ts exists
  referenceUrl?: string; // optional external rulebook / design doc
}

export const GAMES: readonly GameDef[] = [
  {
    id: 'classic-cards',
    name: 'Classic Card Games',
    description: 'Classic card games in one room: War, Texas Hold\'em, Gin Rummy, Hearts and Spades.',
    minPlayers: 2,
    maxPlayers: 8,
    rulesets: [
      {
        id: 'war',
        name: 'War',
        description: 'Each round, play or fold your card. The highest card played scores +1, everyone else who played loses 1, and folding costs 1. First to 5 wins.',
        config: { game: 'war', warVariant: 'regular', warWinningScore: 5 },
      },
      {
        id: 'war-aces-high',
        name: 'War — Aces High',
        description: 'Same as War but Aces always beat any other card.',
        config: { game: 'war', warVariant: 'aces-high', warWinningScore: 5 },
      },
      {
        id: 'texas-holdem',
        name: "Texas Hold'em",
        description: 'No-limit Texas Hold\'em poker. 1000 chips each, 10/20 blinds. Last player standing wins.',
        config: { game: 'texas-holdem', holdemStartingChips: 1000, holdemSmallBlind: 10, holdemBigBlind: 20 },
      },
      {
        id: 'gin-rummy',
        name: 'Gin Rummy',
        description: 'Draw, meld, and knock to win points. First to 100 points wins. 2 players only.',
        config: { game: 'gin-rummy', ginTargetScore: 100 },
      },
      {
        id: 'hearts',
        name: 'Hearts',
        description: 'Trick-taking for 4 players. Avoid hearts and the Q♠. Shoot the moon to flip the scoring. Lowest score when someone hits 100 wins.',
        config: { game: 'hearts', heartsTargetScore: 100 },
      },
      {
        id: 'spades',
        name: 'Spades',
        description: '4-player team game. Bid your tricks, make your bid, collect bags. Nil bid wins big or costs big. First team to 500 wins.',
        config: { game: 'spades', spadesTargetScore: 500, spadesNilBonus: 100, spadesBagPenalty: 100 },
      },
    ],
    status: 'playable',
    hasModule: true,
  },
  {
    id: 'lodden-thinks',
    name: 'Lodden Thinks',
    description: 'Bid on what number a target player secretly chose. Closest bid wins the round.',
    minPlayers: 3,
    maxPlayers: 10,
    rulesets: [],
    status: 'playable',
    hasModule: true,
  },
  {
    id: 'baduk-analysis',
    name: 'Baduk',
    description: 'Analysis tool and AI opponent for the ancient game of Go (Baduk).',
    minPlayers: 1,
    maxPlayers: 2,
    rulesets: [
      {
        id: '19x19',
        name: '19×19 (Full)',
        description: 'Standard 19×19 board. Tournament standard. Komi 6.5.',
        config: { komi: 6.5, handicap: 0, boardSize: 19 },
      },
      {
        id: '13x13',
        name: '13×13 (Medium)',
        description: 'Medium 13×13 board. Good for learning or shorter games. Komi 6.5.',
        config: { komi: 6.5, handicap: 0, boardSize: 13 },
      },
      {
        id: '9x9',
        name: '9×9 (Small)',
        description: 'Small 9×9 board. Fast games, ideal for beginners. Komi 6.5.',
        config: { komi: 6.5, handicap: 0, boardSize: 9 },
      },
    ],
    status: 'playable',
    hasModule: true,
  },
  {
    id: 'heist-city',
    name: 'Heist City',
    description: 'Squad-based heist skirmish. Control a crew of 5, steal objectives, and escape clean — while the Heist Decks make the city react to how you play.',
    minPlayers: 2,
    maxPlayers: 2,
    rulesets: [],
    status: 'in-progress',
    hasModule: true,
    referenceUrl: 'https://docs.google.com/document/d/13kPuC5tJgvY2-_HlFFG0JjOUrJnLEfTpd_TkZFX_Ty4/edit?tab=t.0',
  },
  {
    id: 'dice-factory',
    name: 'Dice Factory',
    description: 'Build a factory of dice with special abilities. Use slot-based abilities, buy cards, and race to 10 VP.',
    minPlayers: 1,
    maxPlayers: 5,
    rulesets: [
      {
        id: 'v021',
        name: 'v0.2.1',
        description: 'Slot-based abilities and factory cards. Race to 10 VP.',
        config: { version: 'v0.2.1', abilityTiers: [1, 2, 3, 4], selectedAbilities: null },
      },
      {
        id: 'v015',
        name: 'v0.1.5 (legacy)',
        description: 'Pip-based economy with straight and set scoring.',
        config: { version: 'v0.1.5', maxRounds: 6 },
      },
      {
        id: 'v03',
        name: 'v0.3 (sandbox)',
        description: 'Per-player dice pools with 12 ability slots and free-form VP/pip tracking. Sandbox for testing concepts.',
        config: { version: 'v0.3' },
      },
    ],
    status: 'in-progress',
    hasModule: true,
  },
  {
    id: 'henhur',
    name: 'HenHur',
    description: 'Racing and auction card game. Three laps around the track with a token economy and card drafting.',
    minPlayers: 2,
    maxPlayers: 6,
    rulesets: [],
    status: 'in-progress',
    hasModule: true,
  },
  {
    id: 'kill-team-draft',
    name: 'Kill Team Draft',
    description: 'Draft cards to build your kill team deck. Three packs pass around the table — pick one card at a time.',
    minPlayers: 2,
    maxPlayers: 4,
    rulesets: [],
    status: 'in-progress',
    hasModule: true,
  },
  {
    id: 'capture-the-flag',
    name: 'Conquer the Flag',
    description: '1v1 medieval skirmish on a ring of hex cells. Two squads of five claim ground and race to carry the enemy flag back to their HQ.',
    minPlayers: 2,
    maxPlayers: 2,
    rulesets: [],
    status: 'playable',
    hasModule: true,
  },
  {
    id: 'stumble-home',
    name: 'Stumble Home',
    description: 'Silent cooperative trick-taker for 3 players. Navigate a meeple across a triangular grid to the end tile before your cards run out.',
    minPlayers: 3,
    maxPlayers: 3,
    rulesets: [],
    status: 'in-progress',
    hasModule: true,
  },
  {
    id: 'van-life',
    name: 'Van Life',
    description: 'Co-op 2-player trick-taking across Alaska\'s 8 National Parks. One player steers the tracker (zones), the other conquers challenges. Conquer all 8 parks in 5 matches.',
    minPlayers: 2,
    maxPlayers: 2,
    rulesets: [
      {
        id: 'medium',
        name: 'Medium bot',
        description: 'Smart heuristic bot — knows the rules, targets zones and challenges strategically.',
        config: { botStrength: 'medium' },
      },
      {
        id: 'weak',
        name: 'Weak bot',
        description: 'Random bot — plays any valid card at random. Great for learning the rules.',
        config: { botStrength: 'weak' },
      },
      {
        id: 'strong',
        name: 'Strong bot',
        description: 'Perfect-information bot — sees all cards, plans the full match optimally. Very challenging.',
        config: { botStrength: 'strong' },
      },
    ],
    status: 'playable',
    hasModule: true,
  },
] as const;

export function getGame(id: string): GameDef | undefined {
  return GAMES.find(g => g.id === id);
}

export function isValidGame(id: string): boolean {
  return GAMES.some(g => g.id === id);
}

export function getPlayableGames(): readonly GameDef[] {
  return GAMES.filter(g => g.status !== 'placeholder');
}

export function getActiveRuleset(gameId: string, config: Record<string, unknown>): RulesetDef | undefined {
  const game = getGame(gameId);
  if (!game || game.rulesets.length === 0) return undefined;
  const id = config.rulesetId as string | undefined;
  return game.rulesets.find(r => r.id === id) ?? game.rulesets[0];
}
